import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BRAND, HERO_IMAGE } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  InputOTP, InputOTPGroup, InputOTPSlot,
} from "../components/ui/input-otp";
import { toast } from "sonner";
import {
  ArrowRight, ShieldCheck, Mail, Lock, Sparkles, Loader2, ChevronLeft, KeyRound,
  UserRound, Clock, ShieldAlert,
} from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

function BrandMark({ compact }) {
  return (
    <div className="flex items-center gap-5">
      <img
        src={BRAND.logo}
        alt="Moroccan Brothers"
        className="h-28 w-auto max-w-[340px] object-contain sm:h-32"
      />
      {!compact && (
        <div className="border-l border-white/15 pl-5 leading-tight">
          <p className="text-base font-medium uppercase tracking-[0.28em] text-white/90 sm:text-lg">
            Acquisitions CRM
          </p>
        </div>
      )}
    </div>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const { requestLogin, verify2fa, resend2fa, cancel2fa, pending2fa } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [remember, setRemember] = useState(true);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("signin"); // "signin" | "signup"
  // Set when the backend says the account still needs a Moroccan Brothers approval.
  const [awaiting, setAwaiting] = useState(null); // { status: "pending" | "declined", new?: bool }

  const step = awaiting ? "awaiting" : pending2fa ? "otp" : "login";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await requestLogin({ email, password, name: mode === "signup" ? name : undefined });
      if (res.pending) {
        setAwaiting({ status: res.status, new: !!res.new });
      } else if (res.sent) {
        toast.success("Verification code sent", {
          description: `We emailed a 6-digit code to ${email}.`,
        });
      } else if (res.dev_code) {
        toast.success("Verification code generated", {
          description: `Email sending isn't configured yet — use code ${res.dev_code}.`,
          duration: 12000,
        });
      }
    } catch (err) {
      toast.error("Couldn't sign in", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (value) => {
    setLoading(true);
    try {
      await verify2fa(value ?? otp);
      toast.success("Welcome back", { description: "Two-factor verified." });
      navigate("/dashboard");
    } catch (err) {
      toast.error("Verification failed", { description: err.message });
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResend = async () => {
    const res = await resend2fa(password);
    if (res) {
      if (res.sent) {
        toast.success("New code sent to your email");
      } else if (res.dev_code) {
        toast.success("New code generated", { description: `Use code ${res.dev_code}.`, duration: 12000 });
      }
    } else {
      toast.error("Couldn't resend. Go back and sign in again.");
    }
  };

  return (
    <div className="min-h-screen w-full bg-background grid lg:grid-cols-2">
      {/* Left — atmospheric brand panel */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMAGE} alt="Marrakech riad" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/45 to-brand-greenDark/30" />
        <div className="absolute inset-0 tech-grid opacity-40" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="inline-flex w-fit rounded-2xl border border-white/10 bg-black/55 px-6 py-4 backdrop-blur-md">
            <BrandMark />
          </div>
          <div className="max-w-md animate-fade-up">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-brand-green" />
              AI-powered Marrakech acquisitions
            </div>
            <h1 className="font-display text-5xl font-bold leading-[1.05] text-white">
              An acquisitions team <span className="mb-text-gradient">in your pocket.</span>
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              Hunt agents. Score properties with AI. Negotiate hard. Scale from your first
              riad to hundreds — all from one intelligent pipeline.
            </p>
          </div>
          <p className="text-xs text-white/50">{BRAND.tagline}</p>
        </div>
      </div>

      {/* Right — auth forms */}
      <div className="relative flex items-center justify-center p-6 sm:p-10">
        <div className="absolute inset-0 lg:hidden">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-background/90" />
        </div>

        <div className="relative z-10 w-full max-w-sm">
          <div className="mb-8 lg:hidden"><BrandMark /></div>

          {step === "awaiting" ? (
            <div className="animate-fade-up text-center" data-testid="awaiting-approval-panel">
              <div className={`mx-auto grid h-14 w-14 place-items-center rounded-2xl border ${awaiting.status === "declined" ? "border-brand-red/30 bg-brand-red/10" : "border-brand-green/30 bg-brand-green/10"}`}>
                {awaiting.status === "declined"
                  ? <ShieldAlert className="h-6 w-6 text-brand-red" />
                  : <Clock className="h-6 w-6 text-brand-green" />}
              </div>
              <h2 className="mt-5 font-display text-3xl font-semibold text-white">
                {awaiting.status === "declined" ? "Access not approved" : awaiting.new ? "Request received" : "Awaiting approval"}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {awaiting.status === "declined" ? (
                  <>Your access request for <span className="text-white/80">{email}</span> wasn't approved. If you're on the programme, please reach out to your success manager at Moroccan Brothers.</>
                ) : (
                  <>Someone from the Moroccan Brothers team needs to approve <span className="text-white/80">{email}</span> before you can sign in. Please reach out to your success manager if it's urgent — you'll be able to sign in as soon as you're approved.</>
                )}
              </p>
              <Button onClick={() => { setAwaiting(null); setPassword(""); setMode("signin"); }} variant="outline" data-testid="awaiting-back-btn"
                className="mt-7 h-12 w-full border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">
                <ChevronLeft className="h-4 w-4" /> Back to sign in
              </Button>
            </div>
          ) : step === "login" ? (
            <div className="animate-fade-up">
              <h2 className="font-display text-3xl font-semibold text-white">{mode === "signup" ? "Request access" : "Welcome back"}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{mode === "signup" ? "Sign up as a student — a Moroccan Brothers success manager approves every account." : "Sign in to manage your pipeline."}</p>

              <form onSubmit={handleLogin} className="mt-8 space-y-4">
                {mode === "signup" && (
                  <div className="relative">
                    <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="text" required value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Full name" autoComplete="name" data-testid="signup-name-input"
                      className="h-12 bg-input/60 pl-10 border-white/10 focus-visible:ring-brand-red"
                    />
                  </div>
                )}
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email" autoComplete="email" data-testid="auth-email-input"
                    className="h-12 bg-input/60 pl-10 border-white/10 focus-visible:ring-brand-red"
                  />
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password" autoComplete={mode === "signup" ? "new-password" : "current-password"} data-testid="auth-password-input"
                    className="h-12 bg-input/60 pl-10 border-white/10 focus-visible:ring-brand-red"
                  />
                </div>

                <label className="flex items-center gap-2.5 pt-1 cursor-pointer select-none">
                  <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)}
                    className="data-[state=checked]:bg-brand-red data-[state=checked]:border-brand-red" />
                  <span className="text-sm text-muted-foreground">
                    Keep me signed in on this device <span className="text-white/40">(1 year)</span>
                  </span>
                </label>

                <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
                  className="h-12 w-full bg-brand-red hover:bg-brand-redDark text-white font-medium group">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <span className="flex items-center gap-2">{mode === "signup" ? "Request access" : "Sign in"}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  )}
                </Button>
              </form>

              <div className="my-6 flex items-center gap-4">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs text-muted-foreground">OR</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <Button onClick={handleGoogle} disabled={loading} variant="outline"
                className="h-12 w-full border-white/12 bg-white/[0.03] text-white hover:bg-white/[0.07]">
                <span className="flex items-center gap-2.5"><GoogleIcon /> Continue with Google</span>
              </Button>

              <p className="mt-6 text-center text-sm text-muted-foreground">
                {mode === "signup" ? (
                  <>Already have an account?{" "}
                    <button type="button" onClick={() => setMode("signin")} data-testid="switch-signin" className="font-medium text-brand-red hover:text-brand-green transition-colors">Sign in</button>
                  </>
                ) : (
                  <>No account yet?{" "}
                    <button type="button" onClick={() => setMode("signup")} data-testid="switch-signup" className="font-medium text-brand-red hover:text-brand-green transition-colors">Request access</button>
                  </>
                )}
              </p>

              <div className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-brand-green" />
                Protected by two-factor email verification
              </div>
            </div>
          ) : (
            <div className="animate-fade-up">
              <button onClick={cancel2fa}
                className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white transition-colors">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>

              <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-brand-green/30 bg-brand-green/10">
                <KeyRound className="h-5 w-5 text-brand-green" />
              </div>
              <h2 className="font-display text-3xl font-semibold text-white">Two-factor check</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter the 6-digit code we emailed to{" "}
                <span className="text-white/80">{pending2fa?.email}</span>.
              </p>

              <div className="mt-8 flex justify-center">
                <InputOTP maxLength={6} value={otp}
                  onChange={(v) => { setOtp(v); if (v.length === 6) handleVerify(v); }}>
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i}
                        className="h-14 w-12 rounded-lg border-white/12 bg-input/60 text-xl text-white" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button onClick={() => handleVerify()} disabled={loading || otp.length < 6}
                className="mt-8 h-12 w-full bg-brand-green hover:bg-brand-greenDark text-white font-medium">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
              </Button>

              <p className="mt-5 text-center text-sm text-muted-foreground">
                Didn't get it?{" "}
                <button onClick={handleResend} className="font-medium text-brand-green hover:text-brand-red transition-colors">
                  Resend code
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
